import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeCalendar, type DateRange } from '@src/components/DateRangeCalendar/DateRangeCalendar';
import { Button } from '@src/components/Button/Button';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import './DateRangePicker.css';

export function DateRangePicker({ value, onChange, disabled = false, calendarOnly = false }: { disabled?: boolean; calendarOnly?: boolean; value: DateRange; onChange: (value: DateRange) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const [custom, setCustom] = useState(calendarOnly);
  useLayoutEffect(() => {
    if (!open || !menu.current) return;
    const popup = menu.current;
    popup.showPopover();
    const position = () => {
      if (!trigger.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft ?? 0) + 12;
      const topEdge = (viewport?.offsetTop ?? 0) + 12;
      const rightEdge = leftEdge + (viewport?.width ?? window.innerWidth) - 24;
      const bottomEdge = topEdge + (viewport?.height ?? window.innerHeight) - 24;
      popup.style.width = `${Math.min(302, rightEdge - leftEdge)}px`;
      const below = Math.max(0, bottomEdge - anchor.bottom - 8);
      const above = Math.max(0, anchor.top - topEdge - 8);
      const placeAbove = below < 320 && above > below;
      popup.style.maxHeight = `${placeAbove ? above : below}px`;
      const height = popup.getBoundingClientRect().height;
      popup.style.left = `${Math.max(leftEdge, Math.min(anchor.left, rightEdge - popup.getBoundingClientRect().width))}px`;
      popup.style.top = `${placeAbove ? Math.max(topEdge, anchor.top - height - 8) : anchor.bottom + 8}px`;
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    window.visualViewport?.addEventListener('resize', position);
    window.visualViewport?.addEventListener('scroll', position);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      window.visualViewport?.removeEventListener('resize', position);
      window.visualViewport?.removeEventListener('scroll', position);
    };
  }, [open, custom]);
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  function choose(next: DateRange) {
    onChange(next);
    setOpen(false);
    trigger.current?.focus();
  }
  return <div className="history-participant-filter" ref={root} onKeyDown={event => {
    if (event.key === 'Escape' && open) { event.stopPropagation(); setOpen(false); trigger.current?.focus(); }
  }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    <Button disabled={disabled} ref={trigger} size="small" variant="secondary" aria-haspopup="dialog" aria-expanded={open} aria-controls={id}
      aria-label={t('historyFilters.time')} icon={<ChevronDownIcon />} iconPosition="end"
      onClick={() => { setCustom(calendarOnly); setOpen(!open); }}>
      {value.start ? `${value.start.slice(8)}.${value.start.slice(5, 7)} – ${value.end.slice(8)}.${value.end.slice(5, 7)}` : t('historyFilters.anyTime')}
    </Button>
    {open && <div ref={menu} popover="manual" id={id} role="dialog" aria-label={t('historyFilters.time')} className="history-participant-filter__menu">
      {custom ? <DateRangeCalendar value={value} onApply={choose} /> : <div className="history-participant-filter__options">
        <button type="button" onClick={() => choose({ start: '', end: '' })}>{t('historyFilters.anyTime')}</button>
        {[0, 1, 2, 6].map(offset => <button type="button" key={offset} onClick={() => {
          const end = new Date(); if (offset === 1) end.setDate(end.getDate() - 1);
          const start = new Date(end); if (offset >= 2) start.setDate(start.getDate() - offset);
          const format = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          choose({ start: format(start), end: format(end) });
        }}>{t(`historyFilters.${({ 0: 'today', 1: 'yesterday', 2: 'threeDays', 6: 'sevenDays' } as Record<number, string>)[offset]}`)}</button>)}
        <button type="button" onClick={() => setCustom(true)}>{t('historyFilters.custom')}</button>
      </div>}
    </div>}
  </div>;
}
